/**
 * ACTIVITY-CORE — human feed texts (i18n) for Vigía Fleet.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { ActivityRow } from "@fleet/plugins/activity";

interface ActivityFieldChange {
  old?: unknown;
  new?: unknown;
}

type ActivityPatch = Record<string, ActivityFieldChange>;

function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseActivityPatch(raw: string | null | undefined): ActivityPatch {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const patch: ActivityPatch = {};
    for (const [field, change] of Object.entries(parsed)) {
      if (isFieldChange(change)) {
        patch[field] = { old: change.old, new: change.new };
      }
    }
    return patch;
  } catch {
    return {};
  }
}

const OFFLINE_LAG_THRESHOLD_MS = 60_000;

export function isOfflineEdit(
  row: Pick<ActivityRow, "server_ts" | "client_ts">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  if (!row.server_ts || !row.client_ts) {
    return false;
  }
  const server = Date.parse(row.server_ts);
  const client = Date.parse(row.client_ts);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return false;
  }
  return server - client > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  batchId: string;
  entries: T[];
}

export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batch_id ?? row.id;
    const last = groups.at(-1);
    if (last?.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}

interface ActivitySentence {
  named: MessageDescriptor;
  anonymous: MessageDescriptor;
}

function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (action === "deleted") {
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const VEHICLE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created vehicle "{name}"`, anonymous: msg`created a vehicle` },
  updated: { named: msg`edited vehicle "{name}"`, anonymous: msg`edited a vehicle` },
  trashed: { named: msg`deleted vehicle "{name}"`, anonymous: msg`deleted a vehicle` },
  purged: { named: msg`permanently deleted vehicle "{name}"`, anonymous: msg`deleted a vehicle` },
  restored: { named: msg`restored vehicle "{name}"`, anonymous: msg`restored a vehicle` },
  reverted: {
    named: msg`undid changes on vehicle "{name}"`,
    anonymous: msg`undid a vehicle change`,
  },
};

const GEOFENCE_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created geofence "{name}"`, anonymous: msg`created a geofence` },
  updated: { named: msg`edited geofence "{name}"`, anonymous: msg`edited a geofence` },
  trashed: { named: msg`deleted geofence "{name}"`, anonymous: msg`deleted a geofence` },
  purged: { named: msg`permanently deleted geofence "{name}"`, anonymous: msg`deleted a geofence` },
  restored: { named: msg`restored geofence "{name}"`, anonymous: msg`restored a geofence` },
  reverted: {
    named: msg`undid changes on geofence "{name}"`,
    anonymous: msg`undid a geofence change`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["name", "title", "plate", "label", "vehicle_name"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "vehicle" || row.entity_type === "device"
      ? VEHICLE_TEXTS
      : row.entity_type === "geofence" || row.entity_type === "zone"
        ? GEOFENCE_TEXTS
        : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_DAYS = 7;

export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < WEEK_DAYS * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
